import { useId } from "react";

import { Chip, ChipRow } from "../lib/Chip.tsx";
import { useModalDialog } from "../lib/useModalDialog.ts";
import { CONTACTS_COPY } from "./contacts.constants.ts";
import type { AddContactDraft, AddContactView } from "./contacts.model.ts";

import "../lib/dialog.css";

/**
 * Adding a contact by hand — the one form behind "+ Add contact".
 *
 * A native `<dialog>` opened with `showModal()`, `AssignAgentDialog`'s choice
 * for its list of things that would otherwise have to be written and got
 * right: modal semantics, Escape, a real focus trap, the rest of the document
 * made INERT rather than merely covered, focus returned to whatever opened it,
 * and the top layer.
 *
 * CLOSING DOES NOT DISCARD, and MOUNTED EVEN WHEN SHUT — a stray Escape must
 * not cost what was typed, and it is `close()` rather than unmounting that
 * returns focus to the button. Cancel is the only control that discards.
 *
 * TWO BRANCHES, ONE TOGGLE. On ONDO takes an id and nothing else — the
 * subtitle states the no-name-search rule once, here, rather than letting
 * somebody discover it by typing. Off ONDO takes the firm's own words: a name,
 * and optionally an email (which links the contact to an ONDO identity,
 * mintingly or matchingly — the form deliberately cannot tell), a location and
 * a type.
 *
 * THERE IS NO CHECK-AS-YOU-TYPE. Eligibility needs a subject the caller
 * already holds and a CRM add has none, so the submit is the first and only
 * question — answered by the server with one sentence per branch, shown
 * verbatim in the alert below the fields.
 */
export function AddContactDialog({
  view,
  onSetDraft,
  onSubmit,
  onCancel,
  onClose,
}: {
  view: AddContactView;
  onSetDraft: (patch: Partial<AddContactDraft>) => void;
  onSubmit: () => void;
  onCancel: () => void;
  onClose: () => void;
}) {
  const dialog = useModalDialog(view.open);
  const titleId = useId();
  const field = useId();

  const onOndo = view.draft.mode === "onOndo";

  return (
    <dialog
      ref={dialog}
      className="contact-add"
      aria-labelledby={titleId}
      // Escape and the backdrop both close natively; this is what tells React
      // it happened. Without it the two disagree and the next render calls
      // showModal() on a dialog that is already open.
      onClose={onClose}
    >
      <div className="contact-add__head">
        <h2 id={titleId} className="contact-add__title">
          {CONTACTS_COPY.addTitle}
        </h2>
        <button
          type="button"
          className="contact-add__shut ondo-dialog__shut"
          aria-label={CONTACTS_COPY.close}
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      <ChipRow label={CONTACTS_COPY.addTitle}>
        <Chip pressed={onOndo} onClick={() => onSetDraft({ mode: "onOndo" })}>
          {CONTACTS_COPY.addModeOn}
        </Chip>
        <Chip pressed={!onOndo} onClick={() => onSetDraft({ mode: "offOndo" })}>
          {CONTACTS_COPY.addModeOff}
        </Chip>
      </ChipRow>

      <p className="contact-add__subtitle">
        {onOndo ? CONTACTS_COPY.addOnSubtitle : CONTACTS_COPY.addOffSubtitle}
      </p>

      {onOndo ? (
        <div className="contact-add__field">
          <label className="contact-add__label" htmlFor={`${field}-id`}>
            {CONTACTS_COPY.addIdLabel}
          </label>
          <input
            id={`${field}-id`}
            className="contact-add__input"
            value={view.draft.ondoId}
            placeholder={CONTACTS_COPY.addIdPlaceholder}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => onSetDraft({ ondoId: event.target.value })}
          />
        </div>
      ) : (
        <>
          <div className="contact-add__field">
            <label className="contact-add__label" htmlFor={`${field}-name`}>
              {CONTACTS_COPY.addNameLabel}
            </label>
            <input
              id={`${field}-name`}
              className="contact-add__input"
              value={view.draft.name}
              placeholder={CONTACTS_COPY.addNamePlaceholder}
              autoComplete="off"
              onChange={(event) => onSetDraft({ name: event.target.value })}
            />
          </div>
          <div className="contact-add__field">
            <label className="contact-add__label" htmlFor={`${field}-email`}>
              {CONTACTS_COPY.addEmailLabel}
            </label>
            <input
              id={`${field}-email`}
              className="contact-add__input"
              type="email"
              value={view.draft.email}
              placeholder={CONTACTS_COPY.addEmailPlaceholder}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => onSetDraft({ email: event.target.value })}
            />
          </div>
          <div className="contact-add__grid">
            <div className="contact-add__field">
              <label className="contact-add__label" htmlFor={`${field}-location`}>
                {CONTACTS_COPY.addLocationLabel}
              </label>
              <input
                id={`${field}-location`}
                className="contact-add__input"
                value={view.draft.location}
                placeholder={CONTACTS_COPY.addLocationPlaceholder}
                autoComplete="off"
                onChange={(event) => onSetDraft({ location: event.target.value })}
              />
            </div>
            <div className="contact-add__field">
              <label className="contact-add__label" htmlFor={`${field}-kind`}>
                {CONTACTS_COPY.addKindLabel}
              </label>
              <select
                id={`${field}-kind`}
                className="contact-add__input"
                value={view.draft.kind}
                onChange={(event) => onSetDraft({ kind: event.target.value })}
              >
                <option value="">{CONTACTS_COPY.addKindNone}</option>
                {view.kindOptions.map((kind) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </>
      )}

      {view.malformed && (
        <p className="contact-add__problem" role="alert">
          {CONTACTS_COPY.addMalformed}
        </p>
      )}

      {/* The server's own sentence — one refusal per branch, no second
          explanation here. A new keystroke clears it in the controller. */}
      {!view.malformed && view.problem && (
        <p className="contact-add__problem" role="alert">
          {view.problem}
        </p>
      )}

      <p className="contact-add__private">{CONTACTS_COPY.addPrivate}</p>

      <div className="contact-add__actions">
        {/* Cancel is the only control that discards. See the header. */}
        <button type="button" className="contact-add__cancel" onClick={onCancel}>
          {CONTACTS_COPY.addCancel}
        </button>
        <button
          type="button"
          className="contact-add__submit"
          disabled={!view.canSubmit}
          onClick={onSubmit}
        >
          {view.busy ? CONTACTS_COPY.addSubmitting : CONTACTS_COPY.addSubmit}
        </button>
      </div>
    </dialog>
  );
}
